import { ChangeEvent } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { SpaceBetween } from 'utils/style/extendable';
import { createNewItemLink } from 'store/providers/providerSlice';
import { creatingItemCanceled as cancelCreatingNewItem } from 'store/providers/providerSlice';

import {
  PickerActions,
  PickedItem,
  DescriptionLink,
  CollectionItem,
  ItemAvatar,
  CollectionContent,
} from './styles';
import messages from './Messages';
import ItemView, {
  IComponentProps as IComponentBaseProps,
  IComponentState as IComponentBaseState,
} from './ItemView';
import Icon from 'components/Icon';
import Button from 'components/Button';
import ActionLink from 'components/ActionLink';
import TaskFileExplorerModal from 'containers/Tasks/TasksManagement/TaskFileExplorerModal';
import Input from 'components/Input';
import notify from 'components/Notify';
import { getLanguageBaseOnTZ } from '../../../../utils/LanguageLocalStorage';
import {
  IProviderItemView,
  IProviderItemViewWithData,
} from '../../../../store/providers/providerTypes';
import { blue } from '../../../../utils/style/colors';
import i18n from 'i18next';
import LocalIcon, { LocalIconTypes } from '../../../../ui/LocalIcon';
import { IUsers } from '../../../../store/store';

interface IComponentProps extends IComponentBaseProps {
  listIndex?: number;
  onOpenFolder?: (index: number, id: number) => void;
  users?: IUsers;
}

interface IComponentState extends IComponentBaseState {
  addDescription: boolean;
  hasDescription: boolean;
  relatedProviderItemId: number | null;
  showFileExplorer: boolean;
  activeLanguage: string;
  description: string;
}

class ItemLinkView extends ItemView<IComponentProps, IComponentState> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      ...this.state,
      addDescription: false,
      hasDescription: false,
      relatedProviderItemId: null,
      showFileExplorer: true,
      activeLanguage: getLanguageBaseOnTZ(),
      description: '',
    };
  }

  deleteItemTitle(name: string) {
    return messages.deleteSearchTagTitle(name);
  }

  deleteItemMessage(name: string) {
    return messages.deleteSearchTagMessage(name);
  }

  onOpenFunction(item: IProviderItemViewWithData) {
    return () => {
      const { listIndex } = this.props;
      this.props?.onOpenFolder?.(listIndex! + 1, item.data.id);
    };
  }

  getThumbnail() {
    return ['link.svg', blue];
  }

  onSaveItemLink = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const item = cloneDeep(this.props.item);
    item.data.order = this.props.expectedOrder;
    const sourceItemId = item.data.dadId;
    const destinationItemId = this.state.relatedProviderItemId as number;
    const description = this.state.description;
    this.props
      .dispatch(
        createNewItemLink({
          sourceItemId,
          destinationItemId,
          description,
          tempItemId: item.data.id,
          providerId: this.props.provider.id,
        })
      )
      .catch(() => {
        this.props.dispatch(
          cancelCreatingNewItem({ item: this.props.item.data })
        );
        notify(messages.errorHappensOnCreatingNewFilePleaseTryAgain, 'error');
      });
  };

  getBadgeView() {
    return (
      <li>
        {this.state.activeLanguage !== 'fa' ? (
          <Icon type={'chevron-right'} />
        ) : (
          <Icon type={'chevron-left'} />
        )}
      </li>
    );
  }

  onAddDescription = () => {
    this.setState({
      addDescription: true,
    });
  };

  onChangeDescription = (e: ChangeEvent<HTMLInputElement>) => {
    const description = e.target.value;
    this.setState({
      description,
    });
  };

  handleChangeProviderItem = (
    relatedProviderItemId: IProviderItemView | null
  ) => {
    this.setState({
      relatedProviderItemId: relatedProviderItemId?.id ?? null,
      showFileExplorer: false,
    });
  };

  getCreatingView = () => {
    const [iconType, iconColor] = this.getThumbnail();
    const { provider } = this.props;
    const relatedProviders = {
      [provider.id]: {
        id: provider.id,
        name: provider.name,
        rootDocumentId: provider.rootDocument.id,
      },
    };

    return (
      <CollectionItem>
        <CollectionContent>
          <ItemAvatar>
            <LocalIcon
              iconName={iconType as LocalIconTypes}
              color={iconColor}
              type="lg"
            />
          </ItemAvatar>

          <form onSubmit={this.onSaveItemLink}>
            <PickerActions>
              {this.state.relatedProviderItemId ? (
                <PickedItem>
                  <span>
                    {
                      this.props.items[this.state.relatedProviderItemId].data
                        .name
                    }
                  </span>
                  <ActionLink
                    color="red"
                    onClick={() => this.handleChangeProviderItem(null)}
                  >
                    <Icon type="trash" />
                  </ActionLink>
                </PickedItem>
              ) : (
                <Button
                  color="blue"
                  onClick={this.showFileExplorer}
                  style={{ flex: 1 }}
                >
                  {i18n.t('addContent', { ns: 'documentManager' }) as any}
                </Button>
              )}
              {this.state.showFileExplorer && (
                <TaskFileExplorerModal
                  defaultItem={this.state.relatedProviderItemId}
                  onClose={this.showFileExplorer}
                  onSave={this.handleChangeProviderItem}
                  providers={relatedProviders}
                />
              )}
            </PickerActions>
            {this.state.addDescription ? (
              <Input
                id="itemLinkDescription"
                label={i18n.t('description', { ns: 'documentManager' }) as any}
                name="itemLinkDescription"
                onChange={this.onChangeDescription}
                placeholder={i18n.t('linkDescription', {
                  ns: 'documentManager',
                })}
                type="text"
              />
            ) : (
              <DescriptionLink onClick={this.onAddDescription}>
                {i18n.t('addDescription', { ns: 'documentManager' })}
              </DescriptionLink>
            )}
            <SpaceBetween>
              <Button
                type="submit"
                color="green"
                disabled={!this.state.relatedProviderItemId}
              >
                {i18n.t('confirm', { ns: 'documentManager' }) as any}
              </Button>
              <Button
                color="red"
                onClick={this.handelCancelCreatingNewItemFunction}
              >
                {i18n.t('cancel', { ns: 'documentManager' }) as any}
              </Button>
            </SpaceBetween>
          </form>
        </CollectionContent>
      </CollectionItem>
    );
  };

  showFileExplorer = () => {
    this.setState((prevState) => ({
      showFileExplorer: !prevState.showFileExplorer,
    }));
  };

  newItemPlaceHolder() {
    return messages.namePlaceHolder;
  }

  newItemTitle() {
    return messages.nameTitle;
  }

  getMenuActions(itemId: number, editable: boolean) {
    const menuActions = super.getMenuActions(itemId, editable);

    if (editable)
      menuActions.push({
        order: 0,
        callBack: () => {
          this.props.onOpenItem(this.props.item, 'view');
        },
        message: messages.configuration,
        group: '1-actions',
        icon: 'tool',
      });
    return menuActions;
  }
}

export default ItemLinkView;
